import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, CheckBox, Layer } from 'grommet';
import { Close, StatusWarning } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { LastActivePeriodSelect } from '../forms/LastActivePeriodSelect';
import { ApiError, FacilityService } from '/src/api';
import { Box, EntityIcon, FormCard, InfoBox, RetirementReasonSelect, Text } from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { Facility, ModelName, TRetirementReasonId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { DateString, FormErrors, FormMetadata } from '/src/lib/types';
import { getFormErrors, getModelLabel, getPeriodFromDateString } from '/src/utils';

export type RetireFacilityModalProps = {
  facility: Facility;
  setIsVisible: (isVisible: boolean) => void;
  setShouldRefresh: (shouldRefresh: boolean) => void;
  isCanada?: boolean;
};

export type TRetireFacilityModalForm = {
  last_active_reporting_quarter: DateString;
};

export const RetireFacilityModal: React.FC<RetireFacilityModalProps> = observer(
  ({ facility, setIsVisible, setShouldRefresh, isCanada = false }) => {
    /** Stores **/
    const userStore = useUserStore();
    const globalStore = useGlobalStore();
    const { t } = useTranslation();

    /** State **/
    const [isRetiring, setIsRetiring] = useState(false);
    const [isConfirmed1, setIsConfirmed1] = useState(false);
    const [isConfirmed2, setIsConfirmed2] = useState(false);

    const [reasonId, setReasonId] = useState<TRetirementReasonId>();
    const [reasonText, setReasonText] = useState('');
    const [isOther, setIsOther] = useState(false);

    const [formValues, setFormValues] = useState<TRetireFacilityModalForm>({
      last_active_reporting_quarter: '',
    });
    const [formErrors, setFormErrors] = useState<FormErrors>({});

    /** Computed **/
    const canRetire = userStore.isFuseAdmin;

    const selectedPeriodLabel = getPeriodFromDateString(formValues.last_active_reporting_quarter ?? '', isCanada);

    const hasReason = !!reasonId && (!isOther || !!reasonText.trim());

    /** Methods **/
    const updateFormValue = (key: string, value: DateString) => {
      setFormValues((prev) => ({ ...prev, [key]: value }));
      if (formErrors[key]) {
        setFormErrors((prev) => ({ ...prev, [key]: '' }));
      }
    };

    const retireFacility = async () => {
      if (isRetiring) return;

      const formFields: FormMetadata = {
        last_active_reporting_quarter: {
          label: t('common:lastReportingPeriod'),
          required: true,
        },
        retirement_reason_id: {
          label: t('common:reasonForRetiring'),
          required: true,
        },
        ...(isOther && {
          retirement_reason_other: {
            label: t('common:otherReasonDescription'),
            required: true,
          },
        }),
      };

      const errors = getFormErrors(formFields, {
        last_active_reporting_quarter: formValues.last_active_reporting_quarter,
        retirement_reason_id: reasonId,
        retirement_reason_other: reasonText.trim(),
      });
      if (errors) {
        setFormErrors(errors);
        return;
      }

      try {
        setIsRetiring(true);
        await FacilityService.retire({
          facility_ids: [facility.id],
          last_active_reporting_quarter: formValues.last_active_reporting_quarter ?? '',
          retirement_reason_id: reasonId as TRetirementReasonId,
          retirement_reason_other: isOther ? reasonText.trim() : null,
        });
        setShouldRefresh(true);
        setIsVisible(false);
        toast.success(toastMessages.retireFacility.success);
      } catch (err) {
        globalStore.handleApiError(err as ApiError, toastMessages.retireFacility.error);
      } finally {
        setIsRetiring(false);
      }
    };

    /** Effects **/

    useEffect(() => {
      if (!canRetire) setIsVisible(false);
    }, [canRetire]);

    /** Render **/
    return (
      <Layer
        position="center"
        onEsc={() => setIsVisible(false)}
        onClickOutside={() => setIsVisible(false)}
        responsive={false}
        style={{
          width: 'auto',
          maxWidth: 'min(95vw, 500px)',
          maxHeight: '95vh',
          overflow: 'auto',
          borderRadius: '8px',
        }}
      >
        <FormCard
          width="100%"
          title={`${t('common:retire')} ${getModelLabel(facility, ModelName.Facility)}`}
          icon={<EntityIcon entityName={ModelName.Facility} />}
          onSubmit={() => retireFacility()}
          isLoading={isRetiring}
          hideRequiredText
          saveButtonLabel={t('common:retireButton')}
          saveButtonLoadingLabel={t('common:retiringButton')}
          saveButtonDisabled={!isConfirmed1 || !isConfirmed2 || !hasReason}
          headerChildren={[
            <Button
              key="close"
              pad="none"
              onClick={() => setIsVisible(false)}
              icon={<Close size="20px" />}
              tip={t('common:close')}
            />,
          ]}
        >
          <Box pad={{ horizontal: 'medium', bottom: 'medium' }} gap="medium">
            <InfoBox gap="1rem" direction="row" align="center">
              <StatusWarning size="medium" color="status-warning" />
              <Text>
                {t('facility:retireWarning')} <b>{t('common:after')}</b>{' '}
                {formValues.last_active_reporting_quarter ? selectedPeriodLabel : t('common:theLastReportingPeriod')}{' '}
                <b>{t('facility:retireWarningEnd')}</b>
              </Text>
            </InfoBox>
            <LastActivePeriodSelect
              id="last_active_reporting_quarter"
              label={t('common:lastReportingPeriod')}
              placeholder={t('common:selectPeriod')}
              value={formValues.last_active_reporting_quarter}
              setValue={(value) => updateFormValue('last_active_reporting_quarter', value)}
              clientId={facility.client_id}
              programId={facility.program_id}
              reportingPeriodTypeId={facility.reporting_period_type_id}
              firstActivePeriod={facility.first_active_reporting_quarter}
              error={formErrors['last_active_reporting_quarter']}
              required
              fill="horizontal"
            />
            <RetirementReasonSelect
              entityType="facility"
              reasonId={reasonId}
              setReasonId={setReasonId}
              reasonText={reasonText}
              setReasonText={setReasonText}
              setIsOther={setIsOther}
              reasonError={formErrors['retirement_reason_id']}
              textError={formErrors['retirement_reason_other']}
            />
            <Box gap="small">
              {formValues.last_active_reporting_quarter && hasReason && (
                <InfoBox
                  gap="1rem"
                  direction="row"
                  align="center"
                  background={{ color: 'status-error', dark: true }}
                  pad="medium"
                  round="small"
                >
                  <CheckBox checked={isConfirmed1} onChange={() => setIsConfirmed1(!isConfirmed1)} color="white" />
                  <Text color="white">{t('facility:retireConfirmation1')}</Text>
                </InfoBox>
              )}
              {formValues.last_active_reporting_quarter && hasReason && (
                <InfoBox
                  gap="1rem"
                  direction="row"
                  align="center"
                  background={{ color: 'status-error', dark: true }}
                  pad="medium"
                  round="small"
                >
                  <CheckBox checked={isConfirmed2} onChange={() => setIsConfirmed2(!isConfirmed2)} color="white" />
                  <Text color="white">
                    {t('facility:retireConfirmation2Start')} <b>{t('common:after')}</b> {selectedPeriodLabel}{' '}
                    {t('facility:retireConfirmation2End')}
                  </Text>
                </InfoBox>
              )}
            </Box>
          </Box>
        </FormCard>
      </Layer>
    );
  }
);
